/* ============================================================
   bd-wishlist.js
   assets/bd-wishlist.js

   - localStorage based wishlist
   - Universal selector: .bd-wishlist-btn[data-product-id]
   - Backward compatible with older section classes so nothing
     that already works today breaks (fc-card__wishlist, ct-wishlist)
   - Uses EVENT DELEGATION, so it also works for cards added to the
     page later (Quick View modal, infinite scroll, AJAX-loaded content)
     without needing to re-bind anything
   - Updates header icon count badge via [data-wishlist-count]
   ============================================================ */

(function () {
  'use strict';

  var STORAGE_KEY = 'll_wishlist';
  var BTN_SELECTOR = '.bd-wishlist-btn[data-product-id], .fc-card__wishlist[data-product-id], .ct-wishlist[data-product-id]';

  /* ── Storage helpers ── */
  function getWishlist() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  function saveWishlist(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {}
  }

  function isWishlisted(productId) {
    return getWishlist().indexOf(String(productId)) !== -1;
  }

  function toggleWishlist(productId) {
    var id = String(productId);
    var list = getWishlist();
    var idx = list.indexOf(id);
    if (idx === -1) {
      list.push(id);
    } else {
      list.splice(idx, 1);
    }
    saveWishlist(list);
    return list;
  }

  /* ── Sync visual state of every wishlist button currently on the page ── */
  function syncButtons() {
    document.querySelectorAll(BTN_SELECTOR).forEach(function (btn) {
      var id = String(btn.dataset.productId);
      btn.classList.toggle('is-wishlisted', isWishlisted(id));
    });
  }

  /* ── Update header badge count ── */
  function updateHeaderBadge() {
    var count = getWishlist().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (b) {
      b.textContent = count;
      b.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  /* ── Small helpers for rendering the Wishlist page ── */
  function money(cents) {
    return '₹' + (cents / 100).toFixed(2);
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str == null ? '' : str;
    return div.innerHTML;
  }

  /* ── Render the Wishlist page (templates/page.wishlist.liquid) ──
        Reads the hidden product-data JSON that bd-wishlist-page.liquid
        outputs, filters it down to whatever IDs are saved in
        localStorage, and builds the cards. Safe to call on any page —
        it quietly does nothing if the wishlist page elements aren't
        present in the DOM. ── */
  function renderWishlistPage() {
    var grid = document.getElementById('bd-wishlist-grid');
    var wrap = document.getElementById('bd-wishlist-wrap');
    var empty = document.getElementById('bd-wishlist-empty');
    var dataEl = document.getElementById('bd-wishlist-product-data');

    if (!grid || !wrap || !empty || !dataEl) return;

    var allProducts;
    try {
      allProducts = JSON.parse(dataEl.textContent);
    } catch (e) {
      allProducts = [];
    }

    var wishlistIds = getWishlist();

    var items = wishlistIds
      .map(function (id) {
        return allProducts.find(function (p) { return String(p.id) === String(id); });
      })
      .filter(Boolean);

    // Clean up stale IDs (product deleted/unpublished since it was saved)
    if (items.length !== wishlistIds.length) {
      saveWishlist(items.map(function (p) { return String(p.id); }));
    }

    if (items.length === 0) {
      wrap.style.display = 'none';
      empty.style.display = 'block';
      return;
    }

    empty.style.display = 'none';
    wrap.style.display = 'block';

    grid.innerHTML = items.map(function (p) {
      var priceHtml = '<span class="bd-wl-price">' + money(p.price) + '</span>';
      if (p.compare_at_price && p.compare_at_price > p.price) {
        priceHtml =
          '<span class="bd-wl-price bd-wl-price--sale">' + money(p.price) + '</span>' +
          '<span class="bd-wl-price--compare">' + money(p.compare_at_price) + '</span>';
      }

      var img = p.image ? (p.image.indexOf('http') === 0 ? p.image : 'https:' + p.image) : '';
      var imgHtml = img
        ? '<img src="' + img + '" alt="' + escapeHtml(p.title) + '" class="bd-wl-card__img" loading="lazy">'
        : '<div class="bd-wl-card__img-placeholder"></div>';

      return (
        '<div class="bd-wl-card" data-product-id="' + p.id + '">' +
          '<a href="' + p.url + '" class="bd-wl-card__img-wrap">' + imgHtml + '</a>' +
          '<div class="bd-wl-card__body">' +
            '<a href="' + p.url + '" class="bd-wl-card__name">' + escapeHtml(p.title) + '</a>' +
            '<div class="bd-wl-price-row">' + priceHtml + '</div>' +
            '<div class="bd-wl-card__actions">' +
              '<button type="button" class="bd-wl-atc bd-quickview-btn" data-product-handle="' + p.handle + '"' + (p.available ? '' : ' disabled') + '>' +
                (p.available ? 'Add To Cart' : 'Sold Out') +
              '</button>' +
              '<button type="button" class="bd-wl-remove" data-remove-id="' + p.id + '">Remove</button>' +
            '</div>' +
          '</div>' +
        '</div>'
      );
    }).join('');
  }

  /* ── Event delegation: one listener on document handles every card,
        present now or added to the DOM later ── */
  document.addEventListener('click', function (e) {
    var wishlistBtn = e.target.closest(BTN_SELECTOR);
    if (wishlistBtn) {
      e.preventDefault();
      e.stopPropagation();
      toggleWishlist(wishlistBtn.dataset.productId);
      syncButtons();
      updateHeaderBadge();
      renderWishlistPage();
      return;
    }

    var removeBtn = e.target.closest('[data-remove-id]');
    if (removeBtn) {
      e.preventDefault();
      toggleWishlist(removeBtn.dataset.removeId);
      syncButtons();
      updateHeaderBadge();
      renderWishlistPage();
    }
  });

  document.addEventListener('DOMContentLoaded', function () {
    syncButtons();
    updateHeaderBadge();
    renderWishlistPage();
  });

  /* Expose small API in case other scripts / the wishlist page need it */
  window.LLWishlist = {
    get: getWishlist,
    toggle: toggleWishlist,
    isWishlisted: isWishlisted,
    sync: syncButtons,
    updateBadge: updateHeaderBadge,
    renderWishlistPage: renderWishlistPage
  };
})();